import { useState, useMemo } from "react";
import { Check, ChevronsUpDown, Users } from "lucide-react";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { groupPlayersForPicker, filterGroupedPlayers } from "../lib/playerGrouping";

/**
 * PlayerPicker — single-select dropdown with search + section grouping.
 *
 * Used wherever an admin needs to pick a player (Inventory issue, Finance
 * transaction link, etc). Members are grouped by section first, then deps
 * by section, with alphabetical sort within each section.
 *
 * Props:
 *  - value:   currently-selected player_id (string | null)
 *  - players: full player list (from /api/players)
 *  - sections: full sections list (from /api/sections) — drives sort order
 *  - onChange(playerId | null): called when the user picks (or clears)
 *  - placeholder: trigger label when nothing selected (default "Select player")
 *  - allowClear: when true, shows a "None" option at the top
 *  - testId:  prefix for data-testids
 *
 * Iter-66l.
 */
const PlayerPicker = ({
  value,
  players = [],
  sections = [],
  onChange,
  placeholder = "Select player",
  allowClear = false,
  testId = "player-picker",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const grouped = useMemo(() => groupPlayersForPicker(players, sections), [players, sections]);
  const filtered = useMemo(() => filterGroupedPlayers(grouped, query), [grouped, query]);

  const selected = useMemo(
    () => players.find((p) => p.id === value) || null,
    [players, value]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={`${testId}-trigger`}
        >
          <span className="flex items-center gap-2 min-w-0 truncate">
            <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            {selected ? (
              <span className="truncate">
                {selected.name}
                {selected.player_type === "dep" && (
                  <span className="text-[10px] text-blue-400 ml-1.5">Dep</span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-muted-foreground flex-shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name, email or section…"
            value={query}
            onValueChange={setQuery}
            data-testid={`${testId}-search`}
          />
          <CommandList>
            <CommandEmpty>No players found.</CommandEmpty>
            {allowClear && (
              <>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      onChange?.(null);
                      setOpen(false);
                    }}
                    data-testid={`${testId}-clear`}
                  >
                    <span className="text-muted-foreground italic">— None —</span>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            {filtered.map((group) => (
              <CommandGroup
                key={group.key}
                heading={
                  <span className="text-[10px] uppercase tracking-wider text-primary font-semibold">
                    {group.label}
                    <span className="ml-1.5 text-muted-foreground font-normal normal-case tracking-normal">
                      {group.rows.length}
                    </span>
                  </span>
                }
              >
                {group.rows.map((p) => (
                  <CommandItem
                    key={p.id}
                    value={`${p.name}__${p.id}`}
                    onSelect={() => {
                      onChange?.(p.id);
                      setOpen(false);
                    }}
                    data-testid={`${testId}-item-${p.id}`}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${value === p.id ? "opacity-100" : "opacity-0"}`}
                    />
                    <span className="flex-1 truncate">{p.name}</span>
                    {p.player_type === "dep" && (
                      <span className="text-[10px] text-blue-400 ml-2 flex-shrink-0">Dep</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default PlayerPicker;
